import { h } from 'preact'
import { useContext, useState } from 'preact/hooks'
import Icon from './Icon'
import { Context } from '../context/Context'
import { Actions, Views, ManageSettings, Framework } from '../../enums'
import Body from '../blocks/Body'
import Container from '../blocks/Container'
import Expandable from '../blocks/Expandable'
import Footer from '../blocks/Footer'
import Heading from '../blocks/Heading'
import { ButtonFormat } from '../elements/Button'
import LI from '../elements/LI'
import P from '../elements/P'
import UL from '../elements/UL'
import { TCF } from '../../../../cmp/tcf/TCF'

const ManageSettingsView = () => {
  const globalState = useContext(Context)
  const { dispatch, state } = globalState

  const [expand, setExpand] = useState({} as IExpand)

  const handleAccept = () => dispatch({
    type: Actions.accept
  })

  const handleCheckbox = (event: any) => {
    const checked: boolean = event.target.checked
    const framework: string = event.target.dataset.framework
    const name: ManageSettings = event.target.dataset.name
    const value: string = event.target.value
    return dispatch({
      type: Actions.toggle,
      settings: {
        framework,
        id: parseInt(value),
        property: name,
        status: checked
      }
    })
  }

  const handleExpand = (event: any) => {
    const key = 'data-id'
    const attributes: any = event.target.attributes
    const name: ManageSettings = event.target.dataset.name
    const id: string = attributes[key].value
    setExpand((prevExpand) => {
      const updatedExpand = { ...prevExpand }
      updatedExpand[name] = updatedExpand[name] || {}
      updatedExpand[name][id] = !updatedExpand[name][id]
      return updatedExpand
    })
  }

  const handleSaveAndExit = () => dispatch({ type: Actions.save })
  
  const handleViewChange = (event: any) => {
    const key = 'data-view'
    const target = event.target as HTMLElement
    const view = Views[target.attributes[key].value] || Views.default
    return dispatch({ type: Actions.view, view })
  }
  
  const manageSettingsCopy = state.copy[Views.manageSettings]

  window.$mediavine.newrelic.addPageAction('gdprModal', { layer: '2' })
  window.$mediavine.newrelic.addPageAction('gdprModal', { cmpLanguage: state.language })

  return (
    <Container color={state.color} view={Views.manageSettings}>
      <Heading view={Views.manageSettings}>
        <Heading.Title>
          {manageSettingsCopy.heading}
        </Heading.Title>
        <Heading.Description>
          {manageSettingsCopy.description}
        </Heading.Description>
        <Heading.Button
          color={state.color}
          format={ButtonFormat.primary}
          onClick={handleAccept}
        >
          {manageSettingsCopy.controls.acceptAll}
        </Heading.Button>
      </Heading>
      <Body view={Views.manageSettings}>
        <Body.Group>
          <Body.Heading>
            {manageSettingsCopy.purposes.heading}
          </Body.Heading>
          {manageSettingsCopy.purposes.content.map((item, index) => (
          <Expandable>
            <Expandable.Heading collapse={expand.purposes && !expand.purposes[index + 1]}>
              <Expandable.Title>
                {item.title}
              </Expandable.Title>
              <Expandable.Controls>
                <Expandable.ToggleGroup>
                  <Expandable.ToggleItem>
                    <Expandable.ToggleText>
                      {manageSettingsCopy.label.consent}
                    </Expandable.ToggleText>
                    <Expandable.ToggleWrapper>
                      <Expandable.Toggle
                        id={`mediavine-gdpr-purposeConsents-${index + 1}`}
                        checked={TCF.tcModel.purposeConsents.has(index + 1)}
                        data-framework={Framework.iab}
                        color={state.color}
                        data-name={ManageSettings.purposeConsents}
                        onChange={handleCheckbox}
                        type="checkbox"
                        value={index + 1}
                      />
                      <Expandable.ToggleLabel
                        aria-label={`mediavine-gdpr-purposeConsents-${index + 1}`}
                        htmlFor={`mediavine-gdpr-purposeConsents-${index + 1}`}
                      />
                    </Expandable.ToggleWrapper>
                  </Expandable.ToggleItem>
                  {/* purpose1 does not have a legitimate interest */}
                  { index > 0 && (
                  <Expandable.ToggleItem>
                  <Expandable.ToggleText>
                    {manageSettingsCopy.label.legitimateInterests}
                  </Expandable.ToggleText>
                  <Expandable.ToggleWrapper>
                    <Expandable.Toggle
                      id={`mediavine-gdpr-purposeLegitimateInterests-${index + 1}`}
                      checked={TCF.tcModel.purposeLegitimateInterests.has(index + 1)}
                      data-framework={Framework.iab}
                      color={state.color}
                      data-name={ManageSettings.purposeLegitimateInterests}
                      onChange={handleCheckbox}
                      type="checkbox"
                      value={index + 1}
                    />
                    <Expandable.ToggleLabel
                      aria-label={`mediavine-gdpr-purposeLegitimateInterests-${index + 1}`}
                      htmlFor={`mediavine-gdpr-purposeLegitimateInterests-${index + 1}`}
                    />
                  </Expandable.ToggleWrapper>
                </Expandable.ToggleItem>
                  )}
                </Expandable.ToggleGroup>
                <Expandable.ToggleItem>
                  <Expandable.Button
                    aria-label={`purposes-${index + 1}`} 
                    data-id={index + 1} 
                    data-name="purposes"
                    onClick={handleExpand}
                  >
                    <Icon
                      color={state.color}
                      status={expand.purposes && expand.purposes[index + 1]}
                    />
                  </Expandable.Button>
                </Expandable.ToggleItem>
              </Expandable.Controls>
            </Expandable.Heading>
            <Expandable.Content collapse={expand.purposes && expand.purposes[index + 1]}>
              <P>{item.intro}</P>
              <UL>
                {item.list.map((listItem) => (
                  <LI>{listItem}</LI>
                ))}
              </UL>
            </Expandable.Content>
          </Expandable>
        ))}
        </Body.Group>
        <Body.Group>
          <Body.Heading>
            {manageSettingsCopy.specialPurposes.heading}
          </Body.Heading>
          {manageSettingsCopy.specialPurposes.content.map((item, index) => (
          <Expandable>
            <Expandable.Heading collapse={expand.specialPurposes && expand.specialPurposes[index + 1]}>
              <Expandable.Title>
                {item.title}
              </Expandable.Title>
              <Expandable.Controls>
                <Expandable.ToggleItem>
                  <Expandable.Button
                    aria-label={`specialPurposes-${index + 1}`} 
                    data-id={index + 1} 
                    data-name="specialPurposes"
                    onClick={handleExpand}
                  >
                    <Icon
                      color={state.color}
                      status={expand.specialPurposes && expand.specialPurposes[index + 1]}
                    />
                  </Expandable.Button>
                </Expandable.ToggleItem>
              </Expandable.Controls>
            </Expandable.Heading>
            <Expandable.Content collapse={expand.specialPurposes && expand.specialPurposes[index + 1]}>
              <P>{item.intro}</P>
              <UL>
                {item.list.map((listItem) => (
                  <LI>{listItem}</LI>
                ))}
              </UL>
            </Expandable.Content>
          </Expandable>
        ))}
        </Body.Group>
        <Body.Group>
          <Body.Heading>
            {manageSettingsCopy.features.heading}
          </Body.Heading>
          {manageSettingsCopy.features.content.map((item, index) => (
          <Expandable>
            <Expandable.Heading collapse={expand.features && expand.features[index + 1]}>
              <Expandable.Title>
                {item.title}
              </Expandable.Title>
              <Expandable.Controls>
                <Expandable.ToggleItem>
                  <Expandable.Button
                    aria-label={`features-${index + 1}`} 
                    data-id={index + 1} 
                    data-name="features"
                    onClick={handleExpand}
                  >
                    <Icon
                      color={state.color}
                      status={expand.features && expand.features[index + 1]}
                    />
                  </Expandable.Button>
                </Expandable.ToggleItem>
              </Expandable.Controls>
            </Expandable.Heading>
            <Expandable.Content collapse={expand.features && expand.features[index + 1]}>
              <P>{item.intro}</P>
              <UL>
                {item.list.map((listItem) => (
                  <LI>{listItem}</LI>
                ))}
              </UL>
            </Expandable.Content>
          </Expandable>
        ))}
        </Body.Group>
        <Body.Group>
          <Body.Heading>
            {manageSettingsCopy.specialFeatures.heading}
          </Body.Heading>
          {manageSettingsCopy.specialFeatures.content.map((item, index) => (
          <Expandable>
            <Expandable.Heading collapse={expand.specialFeatures && expand.specialFeatures[index + 1]}>
              <Expandable.Title>
                {item.title}
              </Expandable.Title>
              <Expandable.Controls>
                <Expandable.ToggleGroup>
                  <Expandable.ToggleItem>
                    <Expandable.ToggleText>
                      {manageSettingsCopy.label.consent}
                    </Expandable.ToggleText>
                    <Expandable.ToggleWrapper>
                      <Expandable.Toggle
                        id={`mediavine-gdpr-specialFeatureOptins-${index + 1}`}
                        checked={TCF.tcModel.specialFeatureOptins.has(index + 1)}
                        data-framework={Framework.iab}
                        color={state.color}
                        data-name={ManageSettings.specialFeatureOptins}
                        onChange={handleCheckbox}
                        type="checkbox"
                        value={index + 1}
                      />
                      <Expandable.ToggleLabel
                        aria-label={`mediavine-gdpr-specialFeatureOptins-${index + 1}`}
                        htmlFor={`mediavine-gdpr-specialFeatureOptins-${index + 1}`}
                      />
                    </Expandable.ToggleWrapper>
                  </Expandable.ToggleItem>
                </Expandable.ToggleGroup>
                <Expandable.ToggleItem>
                  <Expandable.Button
                    aria-label={`specialFeatureOptins-${index + 1}`} 
                    data-id={index + 1}
                    data-name="specialFeatures"
                    onClick={handleExpand}
                  >
                    <Icon
                      color={state.color}
                      status={expand.specialFeatures && expand.specialFeatures[index + 1]}
                    />
                  </Expandable.Button>
                </Expandable.ToggleItem>
              </Expandable.Controls>
            </Expandable.Heading>
            <Expandable.Content collapse={expand.specialFeatures && expand.specialFeatures[index + 1]}>
              <P>{item.intro}</P>
              <UL>
                {item.list.map((listItem) => (
                  <LI>{listItem}</LI>
                ))}
              </UL>
            </Expandable.Content>
          </Expandable>
        ))}
        </Body.Group>
      </Body>
      <Footer>
        <Footer.Row view={Views.manageSettings}>
          <Footer.Group childPosition="flex-start">
            <Footer.Button
              data-view={Views.default}
              format={ButtonFormat.inverse}
              onClick={handleViewChange}
            >
              {manageSettingsCopy.controls.goBack}
            </Footer.Button>
            <Footer.Button
              data-view={Views.partnerSettings}
              format={ButtonFormat.inverse}
              onClick={handleViewChange}
            >
              {manageSettingsCopy.controls.viewPartners}
            </Footer.Button>
          </Footer.Group>
          <Footer.Group childPosition="flex-end">
            <Footer.Button
              color={state.color}
              format={ButtonFormat.secondary}
              onClick={handleSaveAndExit}
            >
              {manageSettingsCopy.controls.saveAndExit}
            </Footer.Button>
          </Footer.Group>
        </Footer.Row>
      </Footer>
    </Container>
  )
}

export default ManageSettingsView

interface IExpand {
  [key: string]: {
    [key: string]: boolean
  }
}